import { ref } from "vue";
import swal from "@/_helpers/swal";
import ApiService from "@/_services/api.service";
import {
    IDupliacteMasterProduct,
    IMasterProduct,
    IPatchProductStatusByBatchPlayload
} from "@/models/product/IMasterProduct";
import Axios from "axios"
import { SelectEntityOption } from "@/models/utility/SelectEntityOption";

const fetchProduct = async (
    itemsPerPage: number,
    page: number,
    criteria?: string | undefined,
    supplierUuid?: string | undefined
): Promise<[boolean, number, IMasterProduct[]]> => {
  const isLoading = ref<boolean>(true);
  const totalRecords = ref<number>(0);
  const masterProducts = Array<IMasterProduct>();
  const tableData = ref<Array<IMasterProduct>>(masterProducts);
    const params = {
        itemsPerPage: itemsPerPage,
        page: page,
        searchByAdminProductName: criteria,
        supplierUuid: supplierUuid,
        sortBy: "adminProductName",
    };
    
  try {
    const { data } = await ApiService.get("master-product", {
        params:params,
      headers: { Accept: "application/ld+json" },
    });
    totalRecords.value = data.data["hydra:totalItems"];
    data.data["hydra:member"].forEach((item, index: number) => {
     // item.id = index;
      tableData.value.push(item);
    });
  } catch (error) {
    swal.error();
  }

  isLoading.value = false;
  return [isLoading.value,totalRecords.value, tableData.value];
}

const fetchProductByCompanyUuid = async (
    itemsPerPage: number,
    page: number,
    companyUuid: string,
    criteria?: string | undefined,
    supplierUuid?: string | undefined
): Promise<[boolean, number, IMasterProduct[]]> => {
  const isLoading = ref<boolean>(true);
  const totalRecords = ref<number>(0);
  const masterProducts = Array<IMasterProduct>();
  const tableData = ref<Array<IMasterProduct>>(masterProducts);
    const params = {
        itemsPerPage: itemsPerPage,
        page: page,
        searchByAdminProductName: criteria,
        supplierUuid: supplierUuid,
        sortBy: "adminProductName",
        companyUuid: companyUuid
    };

    if (companyUuid) {
        params.companyUuid = companyUuid;
    }
    
  try {
    const { data } = await ApiService.get("master-product", {
        params:params,
      headers: { Accept: "application/ld+json" },
    });
    totalRecords.value = data.data["hydra:totalItems"];
    data.data["hydra:member"].forEach((item, IMasterProduct) => {
     // item.id = index;
      tableData.value.push(item);
    });
  } catch (error) {
    swal.error();
  }

  isLoading.value = false;
  return [isLoading.value,totalRecords.value, tableData.value];
}

const deleteMasterProduct = async (uuid: string) => {
  try {
    await ApiService.delete(`master-product/disable/${uuid}`);
  } catch (error) {
    swal.error();
  }
}

const getProduct = (uuid) => {
  return Axios.get('/master-product/'+uuid)
  
}

const getShippingMethodeDependingOnSupplier = (supplierUuuid) => {
  // Variable to track if notification has been shown
  let hasNotified = false;
  
  ApiService.get(`suppliers/${supplierUuuid}/shipping_methods?status=1`).then(({data}) => {
    // Show notification only once
    if (!hasNotified) {
      swal.info(`Shipping methods for supplier: ${supplierUuuid}`);
      hasNotified = true;
    }
  });  
}

const updateProductsStatusByBatch = async (params: IPatchProductStatusByBatchPlayload) => {
    try {
        await ApiService.patchWithoutSlug('admin/master_products_status', params);
    } catch (error) {
        swal.error();
    }
}

const downloadSoldProductsCsv = async (startDate: string, endDate: string, supplierId: string | null) => {
  try {
      const filters = supplierId ? `?supplierId=${supplierId}` : '';

      const { data } = await ApiService.get(`admin/export/sold_product/${startDate}/${endDate}${filters}`,
          { responseType: 'blob' }
      );
      
      return data;
  } catch (error) {
      swal.error();
  }
}

const duplicateMasterProduct = async (data: IDupliacteMasterProduct) => {
    try {
       const response = await ApiService.post(`master-product/duplicate`, data);
       return !response.data.hasError;
    } catch (error) {
        return false;
    }
}

const fetchProductRestrictions = async (
    itemsPerPage: number,
    page: number,
    searchByCompany: string,
    orderByName = "asc"
): Promise<[boolean, number, IMasterProduct[]]> => {
    const isLoading = ref<boolean>(true);
    const totalRecords = ref<number>(0);
    const masterProducts = Array<IMasterProduct>();
    const tableData = ref<Array<IMasterProduct>>(masterProducts);
    const params = {
        itemsPerPage: itemsPerPage,
        page: page,
        search: searchByCompany,
        "order[name]": orderByName
    };

    try {
        const { data } = await ApiService.get("/admin/master-product-company", {
            params: params,
            headers: { Accept: "application/ld+json" },
        });
        
        totalRecords.value = data.data["hydra:totalItems"];
        data.data["hydra:member"].forEach((item: IMasterProduct) => {
            tableData.value.push(item);
        });
    } catch (error) {
        swal.error();
    }

    isLoading.value = false;
    return [isLoading.value,totalRecords.value, tableData.value];
}

const fetchProductRestrictionsByCompany = async (
    companyUuid: string,
    itemsPerPage: number,
    page: number,
    searchByProductName: string,
    orderByName = "asc"
): Promise<[boolean, number, IMasterProduct[]]> => {
    const isLoading = ref<boolean>(true);
    const totalRecords = ref<number>(0);
    const masterProducts = Array<IMasterProduct>();
    const tableData = ref<Array<IMasterProduct>>(masterProducts);
    const params = {
        itemsPerPage: itemsPerPage,
        page: page,
        search: searchByProductName,
        "order[name]": orderByName,
    };

    try {
        const { data } = await ApiService.get(`/admin/master-product-company/by-company/${companyUuid}`, {
            params: params,
            headers: { Accept: "application/ld+json" },
        });
        
        totalRecords.value = data.data["hydra:member"][1];
        data.data["hydra:member"][0].forEach((item: IMasterProduct) => {
            tableData.value.push(item);
        });
    } catch (error) {
        swal.error();
    }

    isLoading.value = false;
    return [isLoading.value, totalRecords.value, tableData.value];
};

async function fetchProductOptionBySupplier(supplierUuid: string|null): Promise<SelectEntityOption[]> {
  if (!supplierUuid) {
      return [];
  }
  const response = await ApiService.get(`/master-product?itemsPerPage=1000&page=1&searchByAdminProductName=&supplierUuid=${supplierUuid}&sortBy=adminProductName`);

  return response.data.data.map((val: any) => {
      let productName = val?.properties?.adminProductName ? val.properties.adminProductName?.value : '';
      productName = productName? productName : ' ';
      if (!val?.status?.active) {
          productName = 'Inactif -  ' + productName;
      }
      return {
          label: productName,
          value: val.uuid,
      }
  });
}

const downloadSearchHistory = async (startDate: string, endDate: string) => {
    try {
        const { data } = await ApiService.get(`admin/last-products-searched/${startDate}/${endDate}`,
            { responseType: 'blob' }
        );
        
        return data;
    } catch (error) {
        swal.error();
    }
}
const totalRecords = ref<number>(0);
const productLocations = async (productUuid: string, params?: any) => {
    try {
        let url = `admin/product/${productUuid}/geolocation`;
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    queryParams.append(key, String(value));
                }
            });
            
            const queryString = queryParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
        }
        
        const response = await ApiService.get(url, {
            headers: { Accept: "application/ld+json" }
        });
        
        if (response.data && response.data.data && response.data.data['hydra:totalItems'] !== undefined) {
            totalRecords.value = response.data.data['hydra:totalItems'];
            
            return {
                totalItems: totalRecords.value,
                items: response.data.data["hydra:member"]
            };
        } else {
            totalRecords.value = 0;
            return {
                totalItems: 0,
                items: []
            };
        }
    } catch (error) {
        totalRecords.value = 0;
        throw error;
    }
};

const searchGeolocations = async (productUuid: string, field: string, value: string, paginationParams?: any) => {
    try {
        // Start with the basic search parameter
        const params: Record<string, string> = { [field]: value };
        
        // Add pagination parameters if provided
        if (paginationParams) {
            Object.entries(paginationParams).forEach(([key, val]) => {
                if (val !== null && val !== undefined && val !== '') {
                    params[key] = String(val);
                }
            });
        }
        
        // Build query string
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, val]) => {
            queryParams.append(key, val);
        });
        
        const url = `admin/product/${productUuid}/geolocation?${queryParams.toString()}`;
        console.log("Searching geolocations with URL:", url);
        
       
        const response = await ApiService.get(url, {
            headers: { Accept: "application/ld+json" }
        });
        
        return response.data.data;
    } catch (error) {
        console.error("Error searching geolocations:", error);
        throw error;
    }
};



export const productService = {
  getProduct,
  deleteMasterProduct,
  fetchProduct,
  fetchProductByCompanyUuid,
  getShippingMethodeDependingOnSupplier,
  updateProductsStatusByBatch,
  downloadSoldProductsCsv,
  duplicateMasterProduct,
  fetchProductRestrictions,
  fetchProductRestrictionsByCompany,
  fetchProductOptionBySupplier,
  downloadSearchHistory,
  productLocations, 
  searchGeolocations
}
